import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Stack, TextField, Typography } from '@mui/material';
import { apiFetch } from '../utils/apiFetch';
import { GENERAL_SETTINGS_KEY, getTenantStorageKey } from '../utils/tenantSettingsStorage';

const emptyIdentity = { tradeName: '', phone: '', email: '', city: '', state: '' };
const projectIdentity = (company) => ({
  tradeName: company.tradeName || '',
  phone: company.phone || '',
  email: company.email || '',
  city: company.address?.city || '',
  state: company.address?.state || '',
});

const TenantStoreIdentityPanel = ({ apiUrl, session, canEdit, onSaved }) => {
  const [draft, setDraft] = useState(emptyIdentity);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState({ loading: true, saving: false, error: '', saved: false });
  const mounted = useRef(true);
  const isCurrent = () => mounted.current && Boolean(getTenantStorageKey(GENERAL_SETTINGS_KEY, session));
  const headers = { Authorization: `Bearer ${session.token}`, 'Content-Type': 'application/json' };
  const readCompany = async () => {
    const response = await apiFetch(`${apiUrl}/api/settings/company`, { headers });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Não foi possível carregar os dados da loja.');
    if (String(data.company?.id) !== String(session.user.companyId)) {
      throw new Error('Os dados recebidos não pertencem à loja desta sessão.');
    }
    return data.company;
  };

  useEffect(() => {
    mounted.current = true;
    if (!isCurrent()) return () => { mounted.current = false; };
    readCompany().then((company) => {
      if (!isCurrent()) return;
      setDraft(projectIdentity(company));
      setLoaded(true);
      setStatus({ loading: false, saving: false, error: '', saved: true });
    }).catch((error) => {
      if (isCurrent()) setStatus({ loading: false, saving: false, error: error.message, saved: false });
    });
    return () => { mounted.current = false; };
    // This instance is keyed by the authenticated tenant and session below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = (key) => (event) => {
    setDraft((current) => ({ ...current, [key]: event.target.value }));
    setStatus((current) => ({ ...current, saved: false }));
  };

  const save = async () => {
    if (!canEdit || !loaded || !isCurrent() || status.loading || status.saving) return;
    setStatus((current) => ({ ...current, saving: true, error: '', saved: false }));
    try {
      const response = await apiFetch(`${apiUrl}/api/settings/company`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          tradeName: draft.tradeName, phone: draft.phone, email: draft.email,
          address: { city: draft.city, state: draft.state },
        }),
      });
      const data = await response.json();
      if (!isCurrent()) return;
      if (!response.ok) throw new Error(data.error || 'Não foi possível salvar os dados da loja.');
      const company = await readCompany();
      if (!isCurrent()) return;
      setDraft(projectIdentity(company));
      setStatus({ loading: false, saving: false, error: '', saved: true });
      onSaved?.(company);
    } catch (error) {
      if (isCurrent()) setStatus((current) => ({ ...current, saving: false, error: error.message, saved: false }));
    }
  };

  return (
    <Card className="configuracoes-card" sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={2}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" color="text.secondary">Dados da loja</Typography>
            <Typography variant="h6">Identidade e contato</Typography>
          </Stack>
          {status.loading || status.saving ? <CircularProgress size={20} /> : (
            <Chip label={status.saved ? 'Atualizado' : 'Não salvo'} color={status.saved ? 'success' : 'default'} variant="outlined" />
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {status.error && <Alert severity="error" sx={{ mb: 2 }}>{status.error}</Alert>}
        <Box className="configuracoes-form-grid">
          {[
            ['tradeName', 'Nome fantasia'], ['city', 'Cidade'], ['state', 'UF'],
            ['phone', 'Telefone de contato'], ['email', 'E-mail de suporte'],
          ].map(([key, label]) => (
            <TextField key={key} label={label} value={draft[key]} onChange={change(key)}
              disabled={!canEdit || !loaded || status.loading || status.saving} fullWidth />
          ))}
        </Box>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={save} disabled={!canEdit || !loaded || status.loading || status.saving || status.saved}>
            {status.saving ? 'Salvando...' : 'Salvar dados da loja'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

const StoreIdentityPanel = (props) => {
  const { session } = props;
  if (!session?.token || !session?.user?.companyId) return null;
  return <TenantStoreIdentityPanel key={`${session.user.companyId}:${session.token}`} {...props} />;
};

export default StoreIdentityPanel;
